import { css, keyframes, styled } from 'styled-components';

import { type CheckboxSize } from './types';

const SIZE_MAP: Record<'height' | 'width' | 'margin', Record<CheckboxSize, string>> = {
  height: { xs: '13px', s: '16px' },
  width: { xs: '13px', s: '16px' },
  margin: { xs: '0', s: '0.25rem' },
};

const INDETERMINATE_SIZE_MAP: Record<'width' | 'margin', Record<CheckboxSize, string>> = {
  width: { xs: '9px', s: '12px' },
  margin: { xs: '5px 1px', s: '6px 1px' },
};

const CHECKED_SIZE_MAP: Record<'width' | 'margin' | 'height', Record<CheckboxSize, string>> = {
  width: { xs: '2px', s: '3px' },
  margin: { xs: '1px 4px', s: '1px 5px' },
  height: { xs: '6px', s: '8px' },
};

const CROSS_MODE_SIZE_MAP: Record<'fontSize' | 'lineHeight', Record<CheckboxSize, string>> = {
  fontSize: {
    xs: '1.25rem',
    s: '1.5rem',
  },
  lineHeight: {
    xs: '0.9rem',
    s: '1rem',
  },
};

const checkAnimation = keyframes`
  0% {
    top: -7px;
    right: -7px;
    bottom: -7px;
    left: -7px;
    opacity: 15%;
  }

  75%{
    opacity: 15%;
  }

  100% {
    top: -1px;
    right: -1px;
    bottom: -1px;
    left: -1px;
    opacity: 0;
  }
`;

const uncheckAnimation = keyframes`
  0% {
    top: -1px;
    right: -1px;
    bottom: -1px;
    left: -1px;
    opacity: 15%;
  }

  75%{
    opacity: 15%;
  }

  100% {
    top: -7px;
    right: -7px;
    bottom: -7px;
    left: -7px;
    opacity: 0;
  }
`;

const indeterminateAnimation = keyframes`
  0% {
    top: -7px;
    right: -7px;
    bottom: -7px;
    left: -7px;
    opacity: 15%;
  }

  75%{
    opacity: 15%;
  }

  100% {
    top: -1px;
    right: -1px;
    bottom: -1px;
    left: -1px;
    opacity: 0;
  }
`;

const Container = styled.label`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  cursor: pointer;
  &[aria-disabled='true'] {
    cursor: default;
  }
  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.designTokens.radioFocusShadow};
  }
`;

const Label = styled.span`
  display: inline-block;
  vertical-align: middle;
  color: ${({ theme }) => theme.palette.darkGrey};
  font-family: ${({ theme }) => theme.typography.fontFamily.regular};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-left: ${({ theme }) => theme.space.component.x.s};
  user-select: none;
  font-weight: 500;
`;

const Box = styled.div<{ size: CheckboxSize }>`
  position: relative;
  display: inline-block;
  height: ${({ size }) => SIZE_MAP.height[size]};
  width: ${({ size }) => SIZE_MAP.width[size]};
  border: solid 1px ${({ theme }) => theme.palette.grey500};
  border-radius: 3px;
  background-color: ${({ theme }) => theme.palette.white};
  margin: ${({ size }) => SIZE_MAP.margin[size]};
  vertical-align: middle;
  box-sizing: border-box;
  &:hover {
    border-color: ${({ theme }) => theme.palette.grey500};
  }
  &:after {
    content: '';
    border-radius: 3px;
    position: absolute;
    background-color: ${({ theme }) => theme.palette.blue400};
    z-index: -1;
  }
`;

const BoxPlaceholder = styled.div<{ size: CheckboxSize }>`
  position: relative;
  display: inline-block;
  height: ${({ size }) => SIZE_MAP.height[size]};
  width: 0;
  margin: ${({ size }) => SIZE_MAP.margin[size]};
  vertical-align: middle;
  box-sizing: border-box;
`;

const Input = styled.input<{ crossMode?: boolean; size: CheckboxSize }>`
  display: none;
  &:checked {
    ~ ${Box} {
      background-color: ${({ theme, crossMode }) => (crossMode ? theme.palette.red400 : theme.palette.blue400)};
      border-color: ${({ theme, crossMode }) => (crossMode ? theme.palette.red400 : theme.palette.blue400)};

      &:before {
        ${({ crossMode, size }) =>
          crossMode &&
          css`
            content: '×';
            position: absolute;
            top: -1px; // border size
            text-align: center;
            width: 100%;
            color: ${({ theme }) => theme.palette.white};
            font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
            font-size: ${CROSS_MODE_SIZE_MAP.fontSize[size]};
            line-height: ${CROSS_MODE_SIZE_MAP.lineHeight[size]};
          `}
        ${({ crossMode, size }) =>
          !crossMode &&
          css`
            content: '';
            transform: rotate(45deg);
            border-bottom: 2px solid ${({ theme }) => theme.palette.white};
            border-right: 2px solid ${({ theme }) => theme.palette.white};
            margin: ${CHECKED_SIZE_MAP.margin[size]};
            width: ${CHECKED_SIZE_MAP.width[size]};
            height: ${CHECKED_SIZE_MAP.height[size]};
          `}
        display: block;
        box-sizing: content-box;
      }
      &:after {
        animation: ${checkAnimation} 0.15s ease-out;
      }
    }
  }
  &:not(:checked) {
    ~ ${Box} {
      &:after {
        animation: ${uncheckAnimation} 0.15s ease-out;
      }
    }
  }
  &:indeterminate {
    ~ ${Box} {
      background-color: ${({ theme }) => theme.palette.blue400};
      border-color: ${({ theme }) => theme.palette.blue400};
      &:before {
        content: '';
        display: block;
        width: ${({ size }) => INDETERMINATE_SIZE_MAP.width[size]};
        height: 2px;
        margin: ${({ size }) => INDETERMINATE_SIZE_MAP.margin[size]};
        border-radius: 3px;
        background-color: ${({ theme }) => theme.palette.white};
        transform: none;
      }
      &:after {
        animation: ${indeterminateAnimation} 0.15s ease-out;
      }
    }
  }
  &:disabled {
    ~ ${Box} {
      border-color: ${({ theme }) => theme.palette.grey400};
      background-color: ${({ theme }) => theme.palette.grey200};
    }
  }
  &:disabled&:indeterminate,
  &:disabled&:checked {
    ~ ${Box} {
      border-color: ${({ theme }) => theme.palette.grey400};
      background-color: ${({ theme }) => theme.palette.grey400};
    }
  }
`;

export const Styled = {
  Container,
  Input,
  Box,
  BoxPlaceholder,
  Label,
};
